import classNames from 'classnames';
import { Controller } from 'react-hook-form';
import I18n from '../../../config/i18n';
import Format from '../../../utils/Formatter';
import { renderArrivalDepartureString, travelerOptions } from '../helpers';
import { getOptionIndex } from '../../../utils/helpers';
import DropdownSelect from '../../../utils/dropdownSelect/DropdownSelect';
import TextInput from '../../../utils/forms/TextInput';
import TextArea from '../../../utils/forms/TextArea';
import CheckboxList from '../../../utils/CheckboxList';
import PriceTooltip from '../../price/components/PriceTooltip';
import AccommodationLink from '../../host/AccommodationLink';

/* eslint-disable react/prop-types */

function totalPrice(
  totalPriceWithoutInsurance,
  cancellationInsuranceCosts,
  addCancellationInsurance,
) {
  if (addCancellationInsurance) {
    return totalPriceWithoutInsurance + cancellationInsuranceCosts;
  }
  return totalPriceWithoutInsurance;
}

function totalPriceAnnotation(isHolidayHome, addCancellationInsurance) {
  if (isHolidayHome) {
    return I18n.t('booking.hh_inc_fees');
  }
  if (addCancellationInsurance) {
    return I18n.t('booking.inc_fees_cancellation');
  }
  return I18n.t('booking.inc_fees');
}

function bubbleOnChange(event, controllerOnChange, legacyOnChange) {
  controllerOnChange(event);
  legacyOnChange(event);
}

function isPetsAllowed(props) {
  let petLimitationLabel = 'Domestic animals';
  if (I18n.locale === 'de') {
    petLimitationLabel = 'Haustiere';
  } else if (I18n.locale === 'es') {
    petLimitationLabel = 'Animales domésticos';
  } else if (I18n.locale === 'fr') {
    petLimitationLabel = 'Animaux domestiques';
  } else if (I18n.locale === 'nl') {
    petLimitationLabel = 'Huisdieren';
  }
  const petsLimitation = props.limitations.find((limit) => limit.label === petLimitationLabel);
  const petsOnRequest = petsLimitation && petsLimitation.value !== 2;

  return props.pets_allowed || petsOnRequest;
}

export default () => [
  {
    legend: I18n.t('booking.journey'),
    content: [
      {
        label: I18n.t('booking.booking_period'),
        field: (props) => (
          <div className="booking-form-time-period">
            <div className="booking-duration-of-stay">
              {I18n.t('booking.duration_of_stay', { count: props.duration_of_stay })}
            </div>
            <div className="booking-arrival-departure annotation">
              {renderArrivalDepartureString(props)}
            </div>
            <AccommodationLink
              accommodation={{ slug: props.accommodation_slug }}
              className="booking-change-period"
            >
              {I18n.t('booking.change')}
            </AccommodationLink>
          </div>
        ),
      },
      {
        label: I18n.t('booking.travelers'),
        field: (props) => (
          <div className="booking-travelers">
            <Controller
              name="number_of_adults"
              control={props.formControl}
              defaultValue={props.number_of_adults || 0}
              render={({ field: { onChange } }) => (
                <DropdownSelect
                  id="number_of_adults"
                  onChange={(value, selectedIndex, e) => {
                    onChange(e);
                    props.onDropdownChange(value, selectedIndex, e);
                  }}
                  options={travelerOptions.adult(props)}
                  selectedIndex={getOptionIndex(
                    props.number_of_adults || 0,
                    travelerOptions.adult(props),
                  )}
                />
              )}
            />
            <Controller
              name="number_of_children"
              control={props.formControl}
              defaultValue={props.number_of_children || 0}
              render={({ field: { onChange } }) => (
                <DropdownSelect
                  id="number_of_children"
                  onChange={(value, selectedIndex, e) => {
                    onChange(e);
                    props.onDropdownChange(value, selectedIndex, e);
                  }}
                  options={travelerOptions.child(props)}
                  selectedIndex={getOptionIndex(
                    props.number_of_children || 0,
                    travelerOptions.child(props),
                  )}
                />
              )}
            />
            <Controller
              name="number_of_infants"
              control={props.formControl}
              defaultValue={props.number_of_infants || 0}
              render={({ field: { onChange } }) => (
                <DropdownSelect
                  id="number_of_infants"
                  onChange={(value, selectedIndex, e) => {
                    onChange(e);
                    props.onDropdownChange(value, selectedIndex, e);
                  }}
                  options={travelerOptions.baby(props)}
                  selectedIndex={getOptionIndex(
                    props.number_of_infants || 0,
                    travelerOptions.baby(props),
                  )}
                />
              )}
            />
            {isPetsAllowed(props) ? (
              <Controller
                name="number_of_pets"
                control={props.formControl}
                defaultValue={props.number_of_pets || 0}
                render={({ field: { onChange } }) => (
                  <DropdownSelect
                    id="number_of_pets"
                    onChange={(value, selectedIndex, e) => {
                      onChange(e);
                      props.onDropdownChange(value, selectedIndex, e);
                    }}
                    options={travelerOptions.pets()}
                    selectedIndex={getOptionIndex(
                      props.number_of_pets || 0,
                      travelerOptions.pets(),
                    )}
                  />
                )}
              />
            ) : (
              <div>{I18n.t('booking.pets_not_allowed')}</div>
            )}
          </div>
        ),
      },
      {
        condition: (props) => props.pets_allowed,
        label: I18n.t('booking.pets'),
        field: (props) => (
          <Controller
            name="number_of_pets"
            control={props.formControl}
            defaultValue={props.number_of_pets || 0}
            render={({ field: { onChange } }) => (
              <DropdownSelect
                id="number_of_pets"
                onChange={(value, selectedIndex, e) => {
                  onChange(e);
                  props.onDropdownChange(value, selectedIndex, e);
                }}
                options={travelerOptions.pets}
                selectedIndex={getOptionIndex(props.number_of_pets || 0, travelerOptions.pets)}
              />
            )}
          />
        ),
      },
    ],
  },
  {
    condition: (props) => props.require_extra_group_information,
    legend: I18n.t('booking.group'),
    content: [
      {
        required: true,
        label: I18n.t('booking.group_min'),
        field: (props) => (
          <Controller
            name="group_min"
            control={props.formControl}
            defaultValue={props.group_min || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="group_min"
                type="number"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('booking.group_max'),
        field: (props) => (
          <Controller
            name="group_max"
            control={props.formControl}
            defaultValue={props.group_max || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="group_max"
                type="number"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('booking.group_comment'),
        field: (props) => (
          <Controller
            name="group_comment"
            control={props.formControl}
            defaultValue={props.groupComment || ''}
            render={({ field: { onChange, value } }) => (
              <TextArea
                id="group_comment"
                value={value}
                maxLength={255}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
    ],
  },
  {
    legend: I18n.t('booking.tenant_data'),
    content: [
      {
        required: true,
        label: I18n.t('booking.tenant_residence'),
        field: ({ insurance_country, onDropdownChange, options, formControl }) => (
          <Controller
            name="insurance_country"
            control={formControl}
            defaultValue={insurance_country || ''}
            render={({ field: { onChange } }) => (
              <DropdownSelect
                id="insurance_country"
                onChange={(value, selectedIndex, e) => {
                  onChange(e);
                  onDropdownChange(value, selectedIndex, e);
                }}
                options={options.insurance_countries}
                selectedIndex={getOptionIndex(insurance_country || '', options.insurance_countries)}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.first_name'),
        field: (props) => (
          <Controller
            name="first_name"
            control={props.formControl}
            defaultValue={props.first_name || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="first_name"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.last_name'),
        field: (props) => (
          <Controller
            name="last_name"
            control={props.formControl}
            defaultValue={props.last_name || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="last_name"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.street_nr'),
        field: (props) => (
          <Controller
            name="invoice_street"
            control={props.formControl}
            defaultValue={props.invoice_street || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="invoice_street"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.postal_code'),
        field: (props) => (
          <Controller
            name="invoice_postal_code"
            control={props.formControl}
            defaultValue={props.invoice_postal_code || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="invoice_postal_code"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.city'),
        field: (props) => (
          <Controller
            name="invoice_city"
            control={props.formControl}
            defaultValue={props.invoice_city || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="invoice_city"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        label: I18n.t('activerecord.attributes.user.phone'),
        field: (props) => (
          <Controller
            name="invoice_phone"
            control={props.formControl}
            defaultValue={props.invoice_phone || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="invoice_phone"
                type="tel"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.cellphone'),
        field: (props) => (
          <Controller
            name="invoice_mobile_phone"
            control={props.formControl}
            defaultValue={props.invoice_mobile_phone || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="invoice_mobile_phone"
                type="tel"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        required: true,
        label: I18n.t('activerecord.attributes.user.email'),
        field: (props) => (
          <Controller
            name="email"
            control={props.formControl}
            defaultValue={props.email || ''}
            render={({ field: { onChange, value } }) => (
              <TextInput
                id="email"
                type="email"
                value={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                onValidate={props.onValidate}
                required
              />
            )}
          />
        ),
      },
      {
        label: () => (
          <div>
            <label htmlFor="booking_comment" className="booking-label-comment">
              {I18n.t('booking.comment')}
            </label>
            <div className="annotation">{I18n.t('booking.comment_label_info')}</div>
          </div>
        ),
        field: (props) => (
          <div>
            <Controller
              name="booking_comment"
              control={props.formControl}
              defaultValue={props.comment || ''}
              render={({ field: { onChange, value } }) => (
                <TextArea
                  id="booking_comment"
                  maxLength={255}
                  value={value}
                  onChange={(e) => bubbleOnChange(e, onChange, props.onTextChange)}
                  onValidate={props.onValidate}
                />
              )}
            />
            <span className="booking-comment-info annotation">
              {I18n.t('booking.comment_note')}
            </span>
          </div>
        ),
      },
      {
        label: '',
        field: (props) => (
          <label htmlFor="newsletter" className="booking-checkbox-newsletter">
            <Controller
              name="newsletter"
              control={props.formControl}
              defaultValue={props.newsletter || false}
              render={({ field: { onChange, value } }) => (
                <input
                  type="checkbox"
                  id="newsletter"
                  className="booking-checkbox"
                  checked={value}
                  onChange={(e) => bubbleOnChange(e, onChange, props.onCheckboxChange)}
                />
              )}
            />
            <span>{I18n.t('booking.newsletter_subscription')}</span>
          </label>
        ),
      },
      {
        label: '',
        field: (props) => (
          <label
            htmlFor="terms_of_service"
            className={classNames('booking-checkbox-tos', { invalid: !props.terms_of_service })}
          >
            <Controller
              name="terms_of_service"
              control={props.formControl}
              defaultValue={props.terms_of_service || false}
              render={({ field: { onChange, value } }) => (
                <input
                  type="checkbox"
                  id="terms_of_service"
                  className="booking-checkbox"
                  checked={value}
                  onChange={(e) => bubbleOnChange(e, onChange, props.onCheckboxChange)}
                  required
                />
              )}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: I18n.t('booking.terms_of_service_html'),
              }}
            />
          </label>
        ),
      },
    ],
  },
  {
    legend: I18n.t('processing.booking.booking_form.payment_method'),
    content: (props) => (
      <div>
        <div
          className={classNames('booking-form-payment-methods-info-wrapper', {
            opened: props.payment_method !== 'bank_transfer',
          })}
        >
          <div className="booking-form-payment-methods-info booking-form-notice">
            {I18n.t('processing.booking.booking_form.payment_method_expl')}
          </div>
        </div>
        <Controller
          name="payment_method"
          control={props.formControl}
          defaultValue={props.payment_method || false}
          render={({ field: { onChange, value } }) => (
            <CheckboxList
              name="payment_method"
              className="booking-form-payment-methods"
              options={props.options.payment_methods}
              selectedIndex={getOptionIndex(value, props.options.payment_methods)}
              onChange={(targetedOption, selectedIndices, e) => {
                onChange(e);
                props.onPaymentChange(targetedOption, selectedIndices, e);
              }}
              radio
              withoutHtml
            />
          )}
        />
      </div>
    ),
  },
  {
    legend: I18n.t('booking.travel_cancelation_insurance_dash'),
    content: (props) => (
      <div className="booking-form-cancellation-insurance">
        <label htmlFor="cancellation-insurance">
          <Controller
            name="add_cancellation_insurance"
            control={props.formControl}
            defaultValue={props.add_cancellation_insurance || false}
            render={({ field: { onChange, value } }) => (
              <input
                type="checkbox"
                id="add_cancellation_insurance"
                checked={value}
                onChange={(e) => bubbleOnChange(e, onChange, props.onCheckboxChange)}
              />
            )}
          />
          <span>
            {I18n.t('booking.cancellation.opt_in')}
            <div className="cancellation-price-label">
              {I18n.t('booking.cancellation.amount', {
                amount: `${I18n.l('currency', props.cancellation_insurance_costs)}`,
              })}
            </div>
            <div
              dangerouslySetInnerHTML={{ __html: I18n.t('booking.cancellation.accept_conditions') }}
            />
          </span>
        </label>
      </div>
    ),
  },
  {
    legend: I18n.t('booking.navigation.booking_info'),
    content: [
      {
        label: I18n.t('booking.price_total'),
        field: (props) => (
          <div className="booking-form-price">
            <div className="booking-form-price-amount">
              {I18n.l(
                'currency',
                totalPrice(
                  props.amount,
                  props.cancellation_insurance_costs,
                  props.add_cancellation_insurance,
                ),
              )}
            </div>
            <div className="booking-form-price-info">
              <div className="price-info-inclusive annotation">
                {totalPriceAnnotation(false, props.add_cancellation_insurance)}
              </div>

              <div className="price-info-exclusive">
                <PriceTooltip
                  extraOptions={props.extraOptions}
                  extraCosts={props.extraCosts}
                  costs={props.costs}
                  isPriced
                />
              </div>
            </div>
          </div>
        ),
      },
    ],
  },
  {
    legend: '',
    content: [
      {
        label: '',
        field: (props) => (
          <button type="submit" className="booking-form-submit" disabled={props.disabled}>
            {I18n.t('buttons.go_to_summary')}
          </button>
        ),
      },
    ],
  },
];

/* eslint-enable react/prop-types */
